import type {
  TargetAxis,
  TargetHistoryEntry,
  TargetItem,
  TargetMetric,
} from "./schemas/program.js";

// CO-7 R4 — read the coach/athlete target audit trail as human-legible CHANGES.
//
// A TargetHistoryEntry is a SNAPSHOT of the targets AFTER a write, not a diff
// (schemas/program.ts). So "12 → 14" has to be DERIVED at display time by
// pairing each entry against the one before it on (axis, key, metric). Two
// invariants fall out of the model and must not be papered over:
//
//  • The FIRST entry has no predecessor — and the POST that creates a program
//    with targets writes NO history at all — so its items read "set to N",
//    never "? → N". An unknown previous value is a third state, not zero
//    ([[feedback_no_claim_without_evidence]]).
//  • `targets` absent on an entry = the targets were CLEARED. Every item the
//    previous snapshot carried is a removal, not a silent disappearance.
//
// Pure + snapshot-only, so it is unit-tested without the route (the route
// handler itself has no test harness in this repo — the derivation is where the
// display logic that can be wrong actually lives).

export type TargetChangeKind = "set" | "changed" | "removed";

export type TargetChange = {
  axis: TargetAxis;
  key: string;
  metric: TargetMetric;
  kind: TargetChangeKind;
  /** Prior value; null for `set` (no predecessor). */
  from: number | null;
  /** New value; null for `removed`. */
  to: number | null;
};

export type TargetHistoryRow = {
  id: string;
  at: string;
  bySub: string;
  changes: TargetChange[];
  /** No prior entry — the first recorded state (all changes are `set`). */
  initial: boolean;
};

const itemsOf = (e: TargetHistoryEntry): TargetItem[] => e.targets?.items ?? [];
const idOf = (it: TargetItem): string => `${it.axis}:${it.key}:${it.metric}`;

/**
 * Derive per-entry target changes by pairing consecutive snapshots on
 * (axis, key, metric). `entries` in chronological order (oldest first, exactly
 * as the history route returns them); rows out NEWEST FIRST for display.
 *
 * An entry whose value/presence diff is empty (e.g. only a band was retuned)
 * still yields a row with an empty `changes` array — the server only appends on
 * a real change, so the row is honest about "something changed here" and the
 * renderer degrades to a generic line rather than dropping the audit entry.
 */
export const deriveTargetHistory = (
  entries: ReadonlyArray<TargetHistoryEntry>,
): TargetHistoryRow[] => {
  const rows: TargetHistoryRow[] = [];
  for (let i = 0; i < entries.length; i++) {
    const e = entries[i]!;
    const cur = itemsOf(e);
    const prev = i > 0 ? itemsOf(entries[i - 1]!) : [];
    const prevById = new Map(prev.map((it) => [idOf(it), it]));
    const curIds = new Set(cur.map(idOf));
    const changes: TargetChange[] = [];

    for (const it of cur) {
      const p = prevById.get(idOf(it));
      if (!p) {
        changes.push({
          axis: it.axis,
          key: it.key,
          metric: it.metric,
          kind: "set",
          from: null,
          to: it.value,
        });
      } else if (p.value !== it.value) {
        changes.push({
          axis: it.axis,
          key: it.key,
          metric: it.metric,
          kind: "changed",
          from: p.value,
          to: it.value,
        });
      }
    }
    for (const it of prev) {
      if (!curIds.has(idOf(it))) {
        changes.push({
          axis: it.axis,
          key: it.key,
          metric: it.metric,
          kind: "removed",
          from: it.value,
          to: null,
        });
      }
    }

    rows.push({
      id: e.id,
      at: e.at,
      bySub: e.bySub,
      changes,
      initial: i === 0,
    });
  }
  return rows.reverse();
};
